import { useEffect, useRef, useState } from 'react';
import {
  Calendar,
  Video,
  PlusCircle,
  Repeat,
  Star,
  Clock,
  ClipboardList,
  CalendarDays,
  Wallet,
  MessageSquare,
} from 'lucide-react';
import { MantaRayIcon } from '@/components/icons/MantaRayIcon';

// Fake data matching real dashboard structure
const FAKE_SESSIONS = [
  { subject: 'Calculus II',    tutor: 'Aman S.',   time: 'Today · 4:00 PM',        dot: '#10b981', joinable: true  },
  { subject: 'AP Physics',     tutor: 'Aaryan R.', time: 'Thu, Mar 6 · 5:30 PM',   dot: '#10b981', joinable: false },
  { subject: 'Linear Algebra', tutor: 'Pavan P.',  time: 'Sat, Mar 8 · 2:00 PM',   dot: '#6366f1', joinable: false },
  { subject: 'Chemistry',      tutor: 'Lara T.',   time: 'Mon, Mar 10 · 11:00 AM',  dot: '#0891b2', joinable: false },
];

const FAKE_PENDING = [
  {
    subject: 'Statistics',
    tutor: 'Nicholas D.',
    status: 'Tutor proposed a time',
    time: 'Wed, Mar 5',
    timeVal: '3:00 PM',
    amber: true,
  },
  {
    subject: 'SAT Prep',
    tutor: null,
    status: 'Finding a tutor...',
    time: null,
    timeVal: null,
    amber: false,
  },
];

const FEEDBACK = [
  { subject: 'Linear Algebra', stars: 5 },
  { subject: 'Calculus I', stars: 4 },
  { subject: 'AP Physics', stars: 5 },
];

// Fake week calendar events: { day 0=Mon..6=Sun, topPct, heightPct, color, label }
const CAL_EVENTS = [
  { day: 0, topPct: 30, heightPct: 18, color: '#10b981', label: 'Calculus II' },
  { day: 2, topPct: 55, heightPct: 14, color: '#6b7280', label: 'AP Physics' },
  { day: 4, topPct: 20, heightPct: 18, color: '#10b981', label: 'Linear Algebra' },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const TIMES = ['3pm', '4pm', '5pm', '6pm', '7pm'];

function Stars({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} className={`h-2.5 w-2.5 ${s <= count ? 'fill-amber-400 text-amber-400' : 'fill-zinc-700 text-zinc-700'}`} />
      ))}
    </div>
  );
}

const CONTENT_WIDTH = 782;
const CONTENT_HEIGHT = 820;
// Heights of chrome bar + in-app navbar at 1:1 scale
const CHROME_H = 44;
const NAVBAR_H = 56;
const BODY_H = CONTENT_HEIGHT - CHROME_H - NAVBAR_H; // visible dashboard body height

export function HeroDashboardMockup() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.6);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setScale(el.offsetWidth / CONTENT_WIDTH);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div>
      <div
        ref={containerRef}
        className="w-full rounded-xl overflow-hidden shadow-2xl border border-border"
        style={{ aspectRatio: `${CONTENT_WIDTH} / ${CONTENT_HEIGHT}` }}
      >
        <div style={{ width: `${CONTENT_WIDTH}px`, transform: `scale(${scale})`, transformOrigin: 'top left', height: `${CONTENT_HEIGHT}px` }}>
        {/* ── Browser chrome ── */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-muted dark:bg-zinc-900 border-b border-border">
          <div className="flex gap-1.5 shrink-0">
            <span className="h-3 w-3 rounded-full bg-red-500/80" />
            <span className="h-3 w-3 rounded-full bg-yellow-500/80" />
            <span className="h-3 w-3 rounded-full bg-green-500/80" />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="bg-background dark:bg-zinc-800 border border-border rounded-md px-4 py-1 text-[11px] text-muted-foreground w-52 text-center">
              mentaratutors.com/dashboard
            </div>
          </div>
        </div>

        {/* ── In-app Navbar ── */}
        <div className="flex items-center justify-between px-6 py-0 bg-background border-b border-border" style={{ height: '56px' }}>
          {/* Logo */}
          <div className="flex items-center gap-2">
            <MantaRayIcon className="h-10 w-10" />
            <span className="text-xl font-bold tracking-tight text-rose-900 dark:text-rose-300">Mentara</span>
          </div>
          {/* Right actions */}
          <div className="flex items-center gap-1.5">
            <div
              className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md"
              style={{ backgroundColor: 'hsl(345, 75%, 31%)', color: 'white' }}
            >
              <PlusCircle className="h-3.5 w-3.5" />
              Request
            </div>
            {[
              { Icon: ClipboardList, label: 'Sessions' },
              { Icon: CalendarDays, label: 'Calendar' },
              { Icon: Wallet, label: 'Payments' },
            ].map(({ Icon, label }) => (
              <div key={label} title={label} className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted">
                <Icon className="h-4.5 w-4.5" style={{ height: '18px', width: '18px' }} />
              </div>
            ))}
            <div className="w-px h-5 bg-border mx-1" />
            <div className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground">
              <MessageSquare style={{ height: '18px', width: '18px' }} />
            </div>
            <div className="h-8 w-8 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center text-[12px] font-semibold text-rose-700 dark:text-rose-300 ml-0.5">
              AJ
            </div>
          </div>
        </div>

        {/* ── Dashboard body — clipped, inner content scrolls with page ── */}
        <div className="bg-background text-foreground overflow-hidden" style={{ height: `${BODY_H}px` }}>
          <div
                        className="px-10 py-6 space-y-4"
          >
          {/* Header */}
            <div>
              <p className="text-2xl font-bold tracking-tight">Welcome back, Alex! 👋</p>
              <p className="text-sm text-muted-foreground">Here's an overview of your activity</p>
            </div>

          {/* ── Top 3 cards (12-col) ── */}
          <div className="grid grid-cols-12 gap-3">
            {/* Upcoming Session — emerald */}
            <div className="col-span-3 rounded-lg border border-emerald-200 dark:border-emerald-900 bg-gradient-to-br from-emerald-50 dark:from-emerald-950 to-background p-3">
              <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-2">Upcoming Session</p>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold truncate">Calculus II</p>
                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold shrink-0">3h</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Aman S.</p>
                  <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium mt-0.5 uppercase tracking-wide">Today · 4:00 PM</p>
                </div>
                <div className="flex items-center gap-1 bg-blue-600 text-white text-[10px] font-medium px-2 py-1 rounded-md shrink-0">
                  <Video className="h-3 w-3" />
                  Join
                </div>
              </div>
            </div>

            {/* Recent Feedback — amber */}
            <div className="col-span-3 rounded-lg border border-amber-200 dark:border-amber-900 bg-gradient-to-br from-amber-50 dark:from-amber-950 to-background p-3">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-2">Recent Feedback</p>
              <div className="space-y-1.5">
                {FEEDBACK.map(f => (
                  <div key={f.subject} className="flex items-center justify-between gap-1">
                    <span className="text-xs font-medium truncate">{f.subject}</span>
                    <Stars count={f.stars} />
                  </div>
                ))}
              </div>
            </div>

            {/* Need Help — stone */}
            <div className="col-span-6 rounded-lg border border-border bg-gradient-to-br from-muted/50 to-background p-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold">Need help with something?</h3>
                  <p className="text-xs text-muted-foreground">Request a tutoring session</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <div className="flex items-center gap-1 border border-border text-muted-foreground text-xs px-2.5 py-1 rounded-md">
                    <Repeat className="h-3 w-3" />
                    Recurring
                  </div>
                  <div className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md" style={{ backgroundColor: 'hsl(345, 75%, 31%)', color: 'white' }}>
                    <PlusCircle className="h-3 w-3" />
                    New Request
                  </div>
                </div>
              </div>
              <div className="border-t border-border mt-3 pt-2.5">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-rose-500 dark:text-rose-400" />
                  <span className="text-xl font-bold text-rose-600 dark:text-rose-300">2</span>
                  <span className="text-muted-foreground text-sm">upcoming in the next week</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Week Calendar ── */}
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b border-border">
              <div className="flex gap-1.5">
                <div className="text-[10px] bg-background border border-border text-muted-foreground px-2 py-0.5 rounded">‹</div>
                <div className="text-[10px] bg-background border border-border text-muted-foreground px-2 py-0.5 rounded">today</div>
                <div className="text-[10px] bg-background border border-border text-muted-foreground px-2 py-0.5 rounded">›</div>
              </div>
              <span className="text-xs font-semibold text-foreground">March 2026</span>
              <div className="flex gap-1">
                <div className="text-[10px] bg-background border border-border text-muted-foreground px-2 py-0.5 rounded">week</div>
              </div>
            </div>

            {/* Grid */}
            <div className="flex" style={{ height: '200px' }}>
              {/* Time axis */}
              <div className="w-12 shrink-0 border-r border-border bg-muted/20 relative">
                {TIMES.map((t, i) => (
                  <div key={t} className="absolute text-[9px] text-muted-foreground/70 pr-1 text-right w-full" style={{ top: `${i * 20}%` }}>{t}</div>
                ))}
              </div>

              {/* Day columns */}
              {DAYS.map((day, di) => {
                const isToday = di === 0; // Mon = today
                const events = CAL_EVENTS.filter(e => e.day === di);
                return (
                  <div
                    key={day}
                    className="flex-1 border-r border-border last:border-r-0 relative"
                    style={{ background: isToday ? 'rgba(239,68,68,0.04)' : di >= 5 ? 'rgba(255,255,255,0.01)' : 'transparent' }}
                  >
                    {/* Day header */}
                    <div className={`text-center py-1 border-b border-border text-[10px] font-medium ${isToday ? 'text-rose-500 dark:text-rose-400' : 'text-muted-foreground'}`}>
                      {day}
                      {isToday && <div className="mx-auto mt-0.5 h-1 w-1 rounded-full bg-rose-500" />}
                    </div>
                    {/* Events */}
                    {events.map((ev, ei) => (
                      <div
                        key={ei}
                        className="absolute left-0.5 right-0.5 rounded text-[8px] font-medium text-white px-1 overflow-hidden"
                        style={{
                          backgroundColor: ev.color,
                          top: `calc(20% + ${ev.topPct}%)`,
                          height: `${ev.heightPct}%`,
                        }}
                      >
                        {ev.label}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Sessions + Pending (grid-cols-3 like real dashboard) ── */}
          <div className="grid grid-cols-3 gap-3">

            {/* My Sessions — col-span-2 */}
            <div className="col-span-2 rounded-lg border border-border bg-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-semibold">My Sessions</p>
                <p className="text-xs text-muted-foreground">Your scheduled tutoring sessions</p>
              </div>
              <div className="p-3 space-y-2">
                {/* Tabs */}
                <div className="flex items-center gap-1 bg-muted rounded-lg p-1 w-fit mb-1">
                  <div className="px-3 py-1 text-xs font-medium bg-background text-foreground rounded-md shadow-sm">Upcoming</div>
                  <div className="px-3 py-1 text-xs font-medium text-muted-foreground">Completed</div>
                </div>
                {/* Session rows */}
                {FAKE_SESSIONS.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 border-l-4" style={{ borderLeftColor: s.dot }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{s.subject}</p>
                      <p className="text-xs text-muted-foreground">{s.tutor} · {s.time}</p>
                    </div>
                    {s.joinable ? (
                      <div className="flex items-center gap-1 bg-blue-600 text-white text-xs font-medium px-2.5 py-1 rounded-md shrink-0">
                        <Video className="h-3 w-3" /> Join
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 border border-border text-muted-foreground text-xs px-2.5 py-1 rounded-md shrink-0">
                        <Clock className="h-3 w-3" /> Scheduled
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Pending — col-span-1 */}
            <div className="col-span-1 rounded-lg border border-border bg-card overflow-hidden h-fit">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                <p className="text-sm font-semibold">Pending</p>
                <span className="ml-auto text-[10px] font-medium bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded-full">2</span>
              </div>
              <div className="p-3 space-y-2">
                {FAKE_PENDING.map((p, i) => (
                  <div
                    key={i}
                    className={`rounded-md p-2.5 border ${p.amber
                      ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800'
                      : 'bg-muted/50 border-border'}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${p.amber ? 'bg-amber-500' : 'bg-blue-400'}`} />
                      <span className="text-sm font-medium truncate flex-1">{p.subject}</span>
                      {p.tutor && <span className="text-[10px] text-muted-foreground shrink-0">{p.tutor}</span>}
                    </div>
                    <div className={`text-xs font-medium mb-1 ${p.amber ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>
                      {p.status}
                    </div>
                    {p.time && (
                      <div className="text-xs flex justify-between mb-1">
                        <span className="text-muted-foreground">{p.time}</span>
                        <span className={`font-semibold ${p.amber ? 'text-amber-700 dark:text-amber-400' : ''}`}>{p.timeVal}</span>
                      </div>
                    )}
                    {p.amber && (
                      <div className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                        Tutor proposed
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</div>
  );
}


